/**
 * NewTicketDialog — file a support ticket. Reusable: the Tickets board opens it,
 * and the global "Report a problem" FAB opens it from any page (passing the page
 * the user was on as `contextPath`).
 *
 * Enriched intake: type-aware questions + auto-captured context (page, app
 * version, browser) folded into `details`. If the page is a demand
 * (/demand/:id), the ticket is auto-linked to that demand.
 */

import { useCallback, useState } from 'react'
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { apiErrorMessage } from '../lib/apiError'
import { useSubmitTicket } from '../hooks/useTickets'
import { useTicketImagePaste } from '../hooks/useTicketImagePaste'
import type { TicketCreateInput, TicketDetails, TicketType } from '../types/ticket'

const TYPES: { value: TicketType; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
]

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '440px' },
  note: { color: tokens.colorNeutralForeground3 },
})

function demandIdFromPath(path: string): number | null {
  const m = path.match(/\/demand\/(\d+)/)
  return m ? Number(m[1]) : null
}

export interface NewTicketDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (id: number) => void
  /** The page the user was on (defaults to the current path). */
  contextPath?: string
}

export function NewTicketDialog({ open, onClose, onCreated, contextPath }: NewTicketDialogProps) {
  const styles = useStyles()
  const submit = useSubmitTicket()
  const [type, setType] = useState<TicketType>('bug')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  // Ids of images pasted while composing. They were uploaded unbound (no ticket
  // existed yet); POST /tickets binds them.
  const [attachmentIds, setAttachmentIds] = useState<number[]>([])
  const [expected, setExpected] = useState('')
  const [steps, setSteps] = useState('')
  const [problem, setProblem] = useState('')
  const [error, setError] = useState<string | null>(null)

  const insertImage = useCallback(
    (md: string) => setBody((prev) => (prev.trim() ? prev + '\n\n' + md : md)),
    [],
  )
  const rememberAttachment = useCallback(
    (img: { id: number }) => setAttachmentIds((prev) => [...prev, img.id]),
    [],
  )
  // No ticketId — this ticket does not exist yet, so images upload unbound and
  // POST /tickets binds them with the ids collected above.
  const imagePaste = useTicketImagePaste({
    onInsert: insertImage,
    onUploaded: rememberAttachment,
    onError: setError,
  })

  const bodyLabel =
    type === 'bug' ? 'What happened?' : type === 'feature' ? 'Describe the feature' : 'Describe it'
  const canSubmit = !!(subject.trim() && body.trim()) && !submit.isPending

  function reset() {
    setType('bug')
    setSubject('')
    setBody('')
    setAttachmentIds([])
    setExpected('')
    setSteps('')
    setProblem('')
    setError(null)
  }

  function onSubmit() {
    if (!canSubmit) return
    const page =
      contextPath ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '')
    const details: TicketDetails = {
      page_url: page || undefined,
      app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    }
    if (type === 'bug') {
      if (expected.trim()) details.expected = expected.trim()
      if (steps.trim()) details.steps = steps.trim()
    } else if (type === 'feature') {
      if (problem.trim()) details.problem = problem.trim()
    }
    const payload: TicketCreateInput = {
      type,
      subject: subject.trim(),
      body: body.trim(),
      details,
      related_demand_id: demandIdFromPath(page) ?? undefined,
      attachment_ids: attachmentIds.length ? attachmentIds : undefined,
    }
    setError(null)
    submit.mutate(payload, {
      onSuccess: (t) => {
        reset()
        onCreated(t.id)
      },
      onError: (err) => setError(apiErrorMessage(err, 'Could not file the ticket.')),
      // The dialog stays open on failure, so an inline message is right here —
      // without one, filing a ticket that the server rejected looked exactly
      // like a dead button.
    })
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New ticket</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {error && (
                <MessageBar intent="error" data-testid="ticket-error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
              <Field label="Type">
                <Dropdown
                  value={TYPES.find((x) => x.value === type)?.label}
                  selectedOptions={[type]}
                  onOptionSelect={(_, d) => setType((d.optionValue as TicketType) ?? 'bug')}
                  data-testid="ticket-type"
                >
                  {TYPES.map((x) => (
                    <Option key={x.value} value={x.value}>
                      {x.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Subject" required>
                <Input value={subject} onChange={(_, d) => setSubject(d.value)} maxLength={255} data-testid="ticket-subject" />
              </Field>
              <Field label={bodyLabel} required>
                <Textarea
                  value={body}
                  onChange={(_, d) => setBody(d.value)}
                  rows={4}
                  data-testid="ticket-body"
                  onPaste={imagePaste.onPaste}
                  onDrop={imagePaste.onDrop}
                  onDragOver={imagePaste.onDragOver}
                />
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {imagePaste.uploading
                    ? 'Uploading image…'
                    : 'Markdown. Paste a screenshot to attach it. Everyone signed in sees tickets.'}
                </Caption1>
              </Field>

              {type === 'bug' && (
                <>
                  <Field label="What did you expect to happen? (optional)">
                    <Input value={expected} onChange={(_, d) => setExpected(d.value)} data-testid="ticket-expected" />
                  </Field>
                  <Field label="Steps to reproduce (optional)">
                    <Textarea value={steps} onChange={(_, d) => setSteps(d.value)} rows={2} data-testid="ticket-steps" />
                  </Field>
                </>
              )}
              {type === 'feature' && (
                <Field label="What problem does it solve? (optional)">
                  <Textarea value={problem} onChange={(_, d) => setProblem(d.value)} rows={2} data-testid="ticket-problem" />
                </Field>
              )}

              <Caption1 className={styles.note}>
                Your page and app version are attached.
              </Caption1>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" onClick={onClose} disabled={submit.isPending}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!canSubmit} onClick={onSubmit} data-testid="ticket-submit">
              {submit.isPending ? 'Filing…' : 'File ticket'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
